import React, {useEffect, useState} from 'react'
import {connect} from 'react-redux'
import {useLocation, useParams} from 'react-router-dom'
import {createStructuredSelector} from 'reselect'
import * as productActions from '../../actions/product'
import * as pdpActions from '../../actions/productDetailPage'
import * as reviewsActions from '../../actions/reviews'
import {isHtoActive} from '../../feature-flags'
import * as pane from '../../store/pane'
import * as pdp from '../../store/productDetailPage'

import {CartContext} from '../../contexts/CartContext'
import NotFound from '../../pages/NotFound'
import ProductImage from '../Carousel/PdpCarousel'
import Desktop from '../Desktop'
import ProductThumbnails from '../Pieces/ProductThumbnails'
import Throbber from '../Throbber'
import FitGuaranteeBanner from './FitGuaranteeBanner'
import PartyAvailableModal from './PartyAvailableModal/PartyAvailableModal'
import PdpHto from './PdpHto'
import ProductFeaturedReviews from './ProductFeaturedReviews'
import ProductHead from './ProductHead'
import ProductOutfitItems from './ProductOutfitItems'
import ProductRichSnippet from './ProductRichSnippet'
import ProductSidebar from './ProductSidebar'
import RecommendedItemsSlider from './RecommendedItemsSlider'
import ProductSoldOutModal from './SoldOutModal/ProductSoldOutModal'
import {DisplayedProduct, SetImage} from './types'
import MobileProductZoom from './Zoom/MobileProductZoom'
import ProductZoomModal from './Zoom/ProductZoomModal'

import '../../../scss/pages/pdp/_pdp.scss'

interface Params {
  product: string
  eventId: string
  orderId: string
}

interface Props {
  displayedProduct: DisplayedProduct
  displayProductDetails: (product: string, origin: string) => void
  isDesktop: boolean
  isHto: boolean
  isOutfit: boolean
  productImages: SetImage[]
  productIsSuitTuxedo: boolean
  selectZoomThumbnail: (index: number) => void
  showBuyPathExperiment: boolean
  isAddItemsFlow?: boolean
  nonIndexableAndNoFollow?: boolean
}

const ProductDetail = ({
  displayedProduct,
  displayProductDetails,
  isDesktop,
  isHto,
  isOutfit,
  productImages,
  productIsSuitTuxedo,
  selectZoomThumbnail,
  showBuyPathExperiment,
  isAddItemsFlow = false,
  nonIndexableAndNoFollow = false,
}: Props) => {
  const {product, eventId, orderId} = useParams<Params>()
  const [isCTLProduct, setIsCTLProduct] = useState(false)
  const {activateCart} = React.useContext(CartContext)
  const {pathname} = useLocation()

  useEffect(() => {
    let origin
    if (showBuyPathExperiment) {
      origin = 'Buy Experiment pdp'
    } else {
      origin = isCTLProduct ? 'completeTheLook' : 'pdp'
    }
    displayProductDetails(product, origin)
  }, [displayProductDetails, isCTLProduct, product, showBuyPathExperiment])

  useEffect(() => {
    if (eventId && orderId && pathname.includes('add-items')) {
      activateCart(Number(eventId), Number(orderId), true)
    }
  }, [eventId, orderId, activateCart, pathname])

  const handleCTLModuleClick = () => setIsCTLProduct(true)

  if (displayedProduct) {
    return (
      <div className="pdp">
        <ProductRichSnippet showBuyPathExperiment={showBuyPathExperiment} />
        {isDesktop ? <ProductZoomModal /> : <MobileProductZoom />}
        <ProductSoldOutModal />
        <PartyAvailableModal />
        <ProductHead
          nonIndexableAndNoFollow={nonIndexableAndNoFollow}
          canonicalHref={showBuyPathExperiment && window.location.href.split('/buy')[0]}
        />
        <section className="product-top-fold">
          <div className="pdp-thumbnails">
            <Desktop>
              <ProductThumbnails
                displayedProduct={displayedProduct}
                modifier="pdp"
                onClick={selectZoomThumbnail}
                productImages={productImages}
              />
            </Desktop>
          </div>
          <ProductImage />
          <ProductSidebar showBuyPathExperiment={showBuyPathExperiment} />
        </section>
        <FitGuaranteeBanner />
        {isOutfit && <ProductOutfitItems isAddItemsFlow={isAddItemsFlow} />}
        {productIsSuitTuxedo && (
          <RecommendedItemsSlider handleCTLModuleClick={handleCTLModuleClick} />
        )}
        <ProductFeaturedReviews />
        {isHto && isHtoActive && <PdpHto />}
      </div>
    )
  }
  if (displayedProduct === false) {
    return <NotFound />
  }
  return <Throbber />
}

const mapStateToProps = createStructuredSelector({
  displayedProduct: pdp.product,
  isDesktop: pane.windowIsDesktop,
  isHto: pdp.productIsHto,
  isOutfit: pdp.productIsOutfit,
  productImages: pdp.productAndModelImages,
  productIsSuitTuxedo: pdp.productIsSuitTuxedo,
})

const mapDispatchToProps = {
  displayProductDetails: productActions.displayProductDetails,
  clearReviews: reviewsActions.clearReviews,
  closeSoldOutModal: pdpActions.closeSoldOutModal,
  closeZoomModal: pdpActions.closeZoomModal,
  resetToInitialState: productActions.resetToInitialState,
  selectZoomThumbnail: pdpActions.selectZoomThumbnail,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetail)
